import { Box, Button, IconButton, Stack, Tooltip } from '@mui/material';
import { Edit, Delete, Visibility } from '@mui/icons-material';
import { isDemoDashboardWriteProtected, notifyDemoWriteBlocked } from '../../utils/demoMode';

interface RowActionsProps {
  onEdit?: () => void;
  onDelete?: () => void;
  onView?: () => void;
  editLabel?: string;
  deleteLabel?: string;
  viewLabel?: string;
  disableDelete?: boolean;
  /** Show text labels beside icons (clearer in dense admin tables). */
  showLabels?: boolean;
}

const labelButtonSx = {
  textTransform: 'none',
  fontWeight: 600,
  minWidth: 0,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  px: 1,
  py: 0.35,
  lineHeight: 1.2,
  fontSize: '0.75rem',
} as const;

const actionTooltipProps = {
  arrow: true,
  placement: 'top' as const,
  enterDelay: 400,
  slotProps: {
    popper: { sx: { zIndex: 1400 } },
  },
};

export default function RowActions({
  onEdit,
  onDelete,
  onView,
  editLabel = 'Edit',
  deleteLabel = 'Delete',
  viewLabel = 'View',
  disableDelete = false,
  showLabels = false,
}: RowActionsProps) {
  if (showLabels) {
    return (
      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="nowrap" sx={{ minWidth: 0 }}>
        {onView && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<Visibility sx={{ fontSize: '15px !important' }} />}
            onClick={onView}
            aria-label={viewLabel}
            sx={{ ...labelButtonSx, color: 'text.secondary', borderColor: 'divider' }}
          >
            {viewLabel}
          </Button>
        )}
        {onEdit && (
          <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<Edit sx={{ fontSize: '15px !important' }} />}
            onClick={onEdit}
            aria-label={editLabel}
            sx={labelButtonSx}
          >
            {editLabel}
          </Button>
        )}
        {onDelete && (
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<Delete sx={{ fontSize: '15px !important' }} />}
            aria-label={deleteLabel}
            disabled={disableDelete}
            sx={labelButtonSx}
            onClick={() => {
              if (isDemoDashboardWriteProtected()) {
                notifyDemoWriteBlocked();
                return;
              }
              onDelete();
            }}
          >
            {deleteLabel}
          </Button>
        )}
      </Stack>
    );
  }

  return (
    <Box display="flex" gap={0.5} alignItems="center" flexWrap="nowrap">
      {onView && (
        <Tooltip title={viewLabel} {...actionTooltipProps}>
          <IconButton size="small" onClick={onView} aria-label={viewLabel} sx={{ color: 'text.secondary' }}>
            <Visibility fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {onEdit && (
        <Tooltip title={editLabel} {...actionTooltipProps}>
          <IconButton size="small" onClick={onEdit} aria-label={editLabel} sx={{ color: 'primary.main' }}>
            <Edit fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {onDelete && (
        <Tooltip title={deleteLabel} {...actionTooltipProps}>
          <Box component="span" sx={{ display: 'inline-flex' }}>
            <IconButton
              size="small"
              color="error"
              aria-label={deleteLabel}
              onClick={() => {
                if (isDemoDashboardWriteProtected()) {
                  notifyDemoWriteBlocked();
                  return;
                }
                onDelete();
              }}
              disabled={disableDelete}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Box>
        </Tooltip>
      )}
    </Box>
  );
}
